import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { authQueryKeys, useMemberships } from '../hooks/auth/use-memberships';
import { useResetConsultantAccessOnUserChange } from '../hooks/auth/use-reset-consultant-access-on-user-change';
import { useSwitchTenant } from '../hooks/auth/use-switch-tenant';
import type { MembershipResponse, MembershipsResponse } from '../services/auth/auth.service';
import { CONSULTANT_NOT_ACTIVE_PARTNER_EVENT } from '../services/ky-client/consultant-not-active-partner';
import { tenantContextAtom } from '../stores/auth.store';
import { consultantAccessNoticeRequestedAtAtom } from '../stores/consultant-access-notice.store';
import { ContactSupportButton } from './contact-support-button';
import { shouldShowConsultantBlockedScreen } from './protected-route.helpers';

/** A burst of failing requests announces the same block many times: show it once. */
const NOTICE_REPEAT_WINDOW_MS = 5000;

function isFullScreenNoticeShown(membershipsResponse: MembershipsResponse | undefined): boolean {
  return (
    membershipsResponse !== undefined &&
    shouldShowConsultantBlockedScreen({
      membershipsPending: false,
      membershipsCount: membershipsResponse.memberships.length,
      consultantAccessBlocked: membershipsResponse.consultantAccessBlocked,
    })
  );
}

function useConsultantAccessNoticeListener() {
  const queryClient = useQueryClient();
  const store = useStore();
  const lastHandledAtRef = useRef(0);

  useEffect(() => {
    const handleNotActivePartner = () => {
      const now = Date.now();
      const isRepeat =
        store.get(consultantAccessNoticeRequestedAtAtom) !== null ||
        now - lastHandledAtRef.current < NOTICE_REPEAT_WINDOW_MS ||
        isFullScreenNoticeShown(
          queryClient.getQueryData<MembershipsResponse>(authQueryKeys.memberships()),
        );
      if (isRepeat) {
        return;
      }
      lastHandledAtRef.current = now;
      store.set(consultantAccessNoticeRequestedAtAtom, now);
      queryClient.invalidateQueries({ queryKey: authQueryKeys.memberships() });
    };
    window.addEventListener(CONSULTANT_NOT_ACTIVE_PARTNER_EVENT, handleNotActivePartner);
    return () =>
      window.removeEventListener(CONSULTANT_NOT_ACTIVE_PARTNER_EVENT, handleNotActivePartner);
  }, [queryClient, store]);
}

function OtherCompanyActions({
  memberships,
  onSwitched,
}: {
  memberships: MembershipResponse[];
  onSwitched: () => void;
}) {
  const { t } = useTranslation();
  const tenantContext = useAtomValue(tenantContextAtom);
  const { switchTenant } = useSwitchTenant();

  const otherMemberships = memberships.filter(
    (membership) => membership.tenantId !== tenantContext?.currentTenantId,
  );
  if (otherMemberships.length === 0) {
    return null;
  }

  const handleSwitch = (tenantId: string, tenantName: string) => {
    switchTenant({ tenantId, tenantName });
    onSwitched();
  };

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      {otherMemberships.map((membership) => (
        <Button
          key={membership.tenantId}
          variant="outlined"
          onClick={() => handleSwitch(membership.tenantId, membership.tenantName)}
          sx={{ minHeight: 48, justifyContent: 'flex-start' }}
        >
          {t('consultant.notActivePartner.switchTo', { name: membership.tenantName })}
        </Button>
      ))}
    </Stack>
  );
}

/**
 * Opens only on a memberships response newer than the 403 that requested it,
 * and only when that response does not hand the app to the full-screen
 * `ConsultantProfileInactiveScreen` (same rule as ProtectedRoute, same data):
 * the user never sees both, not even while the refetch is in flight.
 */
function ConsultantAccessNotice({ requestedAt }: { requestedAt: number }) {
  const { t } = useTranslation();
  const setNoticeRequestedAt = useSetAtom(consultantAccessNoticeRequestedAtAtom);
  const { memberships, consultantAccessBlocked, isPending, respondedAt } = useMemberships();

  const answeredSinceRequest = respondedAt >= requestedAt;
  const fullScreenTakesOver = shouldShowConsultantBlockedScreen({
    membershipsPending: isPending,
    membershipsCount: memberships.length,
    consultantAccessBlocked,
  });
  if (!answeredSinceRequest || fullScreenTakesOver) {
    return null;
  }

  const handleClose = () => setNoticeRequestedAt(null);

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('consultant.notActivePartner.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('consultant.notActivePartner.body')}</DialogContentText>
        <OtherCompanyActions memberships={memberships} onSwitched={handleClose} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handleClose} sx={{ minHeight: 48 }}>
          {t('common.close')}
        </Button>
        <ContactSupportButton />
      </DialogActions>
    </Dialog>
  );
}

/**
 * Blocking notice for a Consulente whose partner profile is no longer active
 * (API answers 403 CONSULTANT_NOT_ACTIVE_PARTNER). Mounted at app level, above
 * ProtectedRoute: the memberships refetch it triggers can swap the whole layout
 * for a spinner or the company picker, and the notice must outlive that swap.
 * Leads with the action (contact support) and offers the other companies, when any.
 */
export function ConsultantNotActivePartnerDialog() {
  const noticeRequestedAt = useAtomValue(consultantAccessNoticeRequestedAtAtom);
  useConsultantAccessNoticeListener();
  useResetConsultantAccessOnUserChange();

  if (noticeRequestedAt === null) {
    return null;
  }
  return <ConsultantAccessNotice requestedAt={noticeRequestedAt} />;
}
